import { useCommonEntitiesStore } from "@/States/commonEntities";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import { RadioGroup } from "@/components/RadioGroup";
import { Card } from "@/components/pageCards/card";

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { meanBy } from "lodash";
import { Customer } from "server/dwhApi/customers/types.js";

import SortingComparisonChart from "./chart";
import { getChartData } from "./getChartData";

export enum ComparisonOptions {
  SIMILAR_TENANTS = "SIMILAR_TENANTS",
  ALL_TENANTS = "ALL_TENANTS",
}

type Props = {
  tenantsData: Customer[];
  tenantCategoryId?: string;
  tenantId?: string;
};

const RecyclingRateRanking: React.FC<Props> = ({
  tenantsData,
  tenantCategoryId,
  tenantId,
}) => {
  const { t, i18n } = useTranslation();
  const { tenantCategories } = useCommonEntitiesStore();
  const [comparisonOption, setComparisonOption] = useState<string>(
    ComparisonOptions.SIMILAR_TENANTS,
  );

  const tenantCategoryName = useMemo(
    () => tenantCategories?.find((c) => c.id === tenantCategoryId)?.name || "",
    [tenantCategories, tenantCategoryId],
  );

  const tenantData = useMemo(
    () => tenantsData.find((d) => d.customerId === tenantId),
    [tenantsData, tenantId],
  );

  const activeCustomers = useMemo(
    () =>
      tenantsData.filter(
        (tenant) =>
          !!tenant.totalOccurrences ||
          tenant.customerId === tenantData?.customerId,
      ),
    [tenantsData, tenantData],
  );

  const chartData = useMemo(
    () =>
      getChartData({
        tenantsData: activeCustomers,
        comparisonOption,
        currentTenant: tenantData as Customer,
        language: i18n.resolvedLanguage,
      }),
    [activeCustomers, tenantData, comparisonOption, i18n],
  );

  const hasNoComparison = useMemo(
    () =>
      !activeCustomers.find(
        (d) =>
          d.customerId !== tenantData?.customerId &&
          d.commercialCustomerCategory ===
            tenantData?.commercialCustomerCategory,
      ),
    [activeCustomers, tenantData],
  );

  useEffect(() => {
    if (!tenantData?.commercialCustomerCategory || hasNoComparison) {
      setComparisonOption(ComparisonOptions.ALL_TENANTS);
    }
  }, [tenantData, setComparisonOption, hasNoComparison]);

  const disabledPopupText = useMemo(
    () =>
      t(
        hasNoComparison
          ? "circularityLabels:noComparisonHint"
          : "circularityLabels:noCategoryHint",
      ),
    [hasNoComparison, t],
  );

  const comparisonOptions = useMemo(
    () => [
      {
        label: hasNoComparison
          ? t("circularityLabels:similarTenantsNoCategory")
          : t("circularityLabels:similarTenants", { tenantCategoryName }),
        value: ComparisonOptions.SIMILAR_TENANTS,
        disabled: !tenantData?.commercialCustomerCategory || hasNoComparison,
        disabledPopupText,
      },
      {
        label: t("circularityLabels:allTenants"),
        value: ComparisonOptions.ALL_TENANTS,
      },
    ],
    [
      hasNoComparison,
      t,
      tenantCategoryName,
      tenantData?.commercialCustomerCategory,
      disabledPopupText,
    ],
  );

  const comparedTenants = useMemo(() => {
    if (comparisonOption === ComparisonOptions.ALL_TENANTS) {
      return activeCustomers;
    }

    if (tenantData) {
      return activeCustomers.filter(
        (d) =>
          d.commercialCustomerCategory ===
          tenantData?.commercialCustomerCategory,
      );
    }

    return [];
  }, [comparisonOption, activeCustomers, tenantData]);

  const averageValue = useMemo(
    () => meanBy(comparedTenants, "sortingDegree") * 100,
    [comparedTenants],
  );

  const hasNoData = useMemo(
    () => !activeCustomers.find((t) => t.sortingDegree > 0),
    [activeCustomers],
  );

  return (
    <Card
      title={t("circularityLabels:comparisonTitle")}
      className="min-w-[450px]"
    >
      {hasNoData ? (
        <div className="flex w-full flex-col justify-stretch">
          <EmptyView type="noDataForInterval" className="min-h-[300px]" />
        </div>
      ) : (
        <div className="flex w-full flex-col justify-between">
          <div className="border-grey1 mb-4 border-b-2 pb-3">
            <h2 className="font-dm-sans mb-1 text-start text-sm font-semibold">
              {t("circularityLabels:comparisonOptionsTitle")}
            </h2>
            <RadioGroup
              onChange={setComparisonOption}
              value={comparisonOption}
              options={comparisonOptions}
            />
          </div>
          <div>
            <h1 className="font-dm-sans mb-5 text-start text-sm font-semibold">
              {t("circularityLabels:outOfNrTenantsLabel", {
                nrTenants: comparedTenants.length,
              })}
            </h1>
          </div>
          <SortingComparisonChart data={chartData} average={averageValue} />
        </div>
      )}
    </Card>
  );
};

export default RecyclingRateRanking;
